import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Box, Button, CircularProgress, createTheme, ThemeProvider, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import { InputField } from '../components/inputs/InputField'
import { inputRequiredRule } from '../helpers/form.helper';
import { UserAuth, UserCredentials } from '../types';
import procesarLogo from '../assets/img/logo.jpeg'
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppContext } from '../context';
import { sessionAdapter } from '../adapter';
import ModalPrivacyNotice from '../components/modal/ModalPrivacyNotice';
import util from '../helpers/util.helper';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useDataClient from '../hooks/useDataClient';
import { GAHelper } from '../helpers/ga.helper';
import { APPTEXT } from '../config/text/apptext.config'
import PublicCarousel from '../components/containers/PublicCarousel';

const darkTheme = createTheme({
    palette: { mode: 'dark' }
});

export default function Login() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const appContext = useContext(AppContext)
    const { control, formState, handleSubmit } = useForm()
    const [openPrivacyNotice, setOpenPrivacyNotice] = useState(false)
    const sessionClient = useDataClient<UserAuth>({ appContext, disableLoading: true })
    const nextPage = useMemo<string>(() => util.restoreNexPageUrl(searchParams, '/dashboard'), [searchParams])
    const onSubmit = useCallback((data: any) => {
        let credentials = data as UserCredentials
        sessionClient.executeRequest(sessionAdapter.login(credentials))
    }, [])
    useDocumentTitle('Login')
    useEffect(() => {
        GAHelper.cleanSession()
    }, [])
    useEffect(() => {
        if (sessionClient.status.data) {
            appContext?.user.setData({
                token: sessionClient.status.data?.token || '',
                user: sessionClient.status.data?.user || ''
            })
            navigate(nextPage)
        }
    }, [sessionClient.status.data])

    useEffect(() => {
        if (appContext?.user.data?.token) {
            navigate(nextPage)
        }
    }, [appContext?.user.data])

    return (<Box className="login">
        <div className='login-form'>

            <ThemeProvider theme={darkTheme}>
                <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                    <div className='form-contain-row d-flex-center resp-display' style={{ gap: '0' }}>
                        <div>
                            <img src={procesarLogo} alt='procesar' className='login-form-logo' />
                        </div>
                        <div className='login-form-title'>
                            <Typography variant='h4'>{APPTEXT.LOGIN.TITLE}</Typography>
                        </div>
                    </div>
                    <div className='form-contain-row'>
                        <div>
                            <InputField
                                controller={{
                                    name: "user",
                                    control: control,
                                    defaultValue: "",
                                    rules: inputRequiredRule
                                }}
                                errors={formState.errors}
                                testid="userTextbox"
                                label="Usuario*"
                                inputProps={{ maxLength: 50 }}
                                autoFocus={true}
                                disabled={sessionClient.status.loading}
                            />
                        </div>
                    </div>
                    <div className='form-contain-row'>
                        <div>
                            <InputField
                                controller={{
                                    name: "password",
                                    control: control,
                                    defaultValue: "",
                                    rules: inputRequiredRule
                                }}
                                type="password"
                                testid="accessTextBox"
                                errors={formState.errors}
                                label="Contraseña*"
                                inputProps={{ maxLength: 20 }}
                                disabled={sessionClient.status.loading}
                            />
                        </div>
                    </div>
                    <div className='form-contain-row'>
                        <div>
                            <Button
                                variant='outlined'
                                fullWidth
                                type="submit"
                                startIcon={!sessionClient.status.loading && <FontAwesomeIcon icon={'sign-in-alt'} />}
                                disabled={sessionClient.status.loading}
                            >
                                {sessionClient.status.loading ? <CircularProgress size={'1.5rem'} /> : APPTEXT.LOGIN.ACTION_LOGIN}
                            </Button>
                            <hr className='divider' />
                            <Button
                                variant='text'
                                fullWidth
                                onClick={() => setOpenPrivacyNotice(true)}
                                disabled={sessionClient.status.loading}
                            >
                                {APPTEXT.LOGIN.ACTION_PRIVACY_NOTICE}
                            </Button>
                        </div>
                    </div>
                </form>
            </ThemeProvider>
        </div>
        <div className='login-media'>
            <PublicCarousel />
            <div className='login-media-footer'>
                <div>®2022 - 2024 | Arquitectura de Software | {process.env.REACT_APP_VERSION}</div>
            </div>
        </div>
        <ModalPrivacyNotice open={openPrivacyNotice} onAccept={() => setOpenPrivacyNotice(false)} />
    </Box>
    )
}
